/**
 * EmployeeLeaveModalV2 - Employee termination modal with Tailwind/shadcn UI.
 * Replaces ChakraUi/Modals/EmployeeLeaveModal.tsx
 */
import React, { useState, useEffect, useContext } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { ArcaContext } from '../../context/ArcaContext.context';
import employeeSoftDelete from '../../api/users/employeeSoftDelete';
import getEmployeeTerminationPreview, {
	EmployeeTerminationPreviewResponse
} from '../../api/users/employeeTerminationPreview';
import { LeaveReasonType } from '../../api/users/interfaces/Users';
import { validatePersonalEmail, validatePhone } from '../../utils/personalContactValidation';

interface EmployeeLeaveModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	user: {
		id: string;
		name: string;
		email: string;
		/**
		 * Contacto personal del empleado. Si falta, el modal lo pide antes de dejar
		 * confirmar la baja: es la única vía para recuperar la cuenta cuando pierda el
		 * correo corporativo. `phone` es NOT NULL en BBDD, así que "sin teléfono" llega
		 * como cadena vacía, no como null.
		 */
		personalEmail?: string | null;
		phone?: string | null;
	};
	translate: (key: string) => string;
}

// El backend responde con este código cuando el correo ya pertenece a otra persona.
// Se traduce a un aviso genérico: al admin le basta con saber que pruebe otro, y no
// tiene por qué enterarse de qué cuentas existen.
const PERSONAL_EMAIL_TAKEN = 'PERSONAL_EMAIL_ALREADY_IN_USE';
// El backend rechaza además que el correo personal sea el propio corporativo del
// empleado: es justo la dirección que va a perder al irse.
const PERSONAL_EMAIL_SAME_AS_CORPORATE = 'PERSONAL_EMAIL_SAME_AS_CORPORATE';

const EmployeeLeaveModalV2: React.FC<EmployeeLeaveModalV2Props> = ({
	isOpen,
	onClose,
	onSuccess,
	user,
	translate
}) => {
	const { token } = useContext(ArcaContext);
	const { toast } = useToast();

	// Helper function for translation with variable interpolation
	const t = (key: string, vars: Record<string, string | number>) => {
		let text = translate(key);
		Object.keys(vars).forEach((varKey) => {
			text = text.replace(`{${varKey}}`, String(vars[varKey]));
		});
		return text;
	};

	const [leaveDate, setLeaveDate] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [preview, setPreview] = useState<EmployeeTerminationPreviewResponse | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [personalEmail, setPersonalEmail] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [personalEmailError, setPersonalEmailError] = useState<string>('');
	const [phoneError, setPhoneError] = useState<string>('');

	// Qué contacto le falta al empleado. Se resuelve al abrir el modal (no al enviar) para
	// que el admin vea de entrada lo que se le va a pedir.
	const needsPersonalEmail = !user.personalEmail?.trim();
	const needsPhone = !user.phone?.trim();

	// Formato de importe en euros con separador de miles (1.300,00 €), coherente con el email
	// de confirmación de baja. useGrouping:true fuerza el separador también en 4 cifras.
	const formatAmount = (amount: number): string =>
		new Intl.NumberFormat('es-ES', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			useGrouping: true
		}).format(amount);

	// Formatea una fecha ISO (YYYY-MM-DD) a dd/mm/yyyy para los textos del panel, tal como en
	// el diseño ("La baja se registrará con fecha 20/06/2026").
	const formatDate = (isoDate: string): string => {
		const [year, month, day] = isoDate.split('-');
		return `${day}/${month}/${year}`;
	};

	// Escenario del CUERPO del panel (párrafo, subtítulo y aportación del empleado). Lo determina
	// si la aportación del MES DE LA BAJA ya se ha ejecutado, no la relación de la fecha con hoy
	// (el título sí depende de eso, ver `dateRelation`).
	//   CURRENT → el día de aportación del mes de la baja ya pasó: la aportación (incluida la
	//             retención del empleado) ya se ejecutó. Cuenta al empleado.
	//   FUTURE  → aún no ejecutada y la fecha de baja es posterior a hoy: baja programada.
	//   RETRO   → aún no ejecutada y la fecha de baja es de hoy o anterior: pendiente este mes.
	const getScenario = (
		isoDate: string,
		contributionDay: number
	): 'RETRO' | 'FUTURE' | 'CURRENT' => {
		const [leaveYear, leaveMonth] = isoDate.split('-').map(Number);
		const now = new Date();
		const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		// La aportación de un mes se ejecuta el día de aportación de ESE mes.
		const closingOfLeaveMonth = new Date(leaveYear, leaveMonth - 1, contributionDay);
		if (closingOfLeaveMonth <= todayMidnight) {
			return 'CURRENT';
		}
		return isoDate > today ? 'FUTURE' : 'RETRO';
	};

	// Get today's date in YYYY-MM-DD format
	const today = (() => {
		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	})();

	// Get end of NEXT month — permite programar bajas del mes que viene (política "mes íntegro
	// en baja": la aportación del mes de baja futura se ejecuta completa el día de aportación).
	const endOfNextMonth = (() => {
		const now = new Date();
		const year = now.getFullYear();
		const month = now.getMonth();
		// new Date(year, month + 2, 0) → último día del mes siguiente (maneja el salto de año)
		const end = new Date(year, month + 2, 0);
		const endYear = end.getFullYear();
		const endMonth = String(end.getMonth() + 1).padStart(2, '0');
		const endDay = String(end.getDate()).padStart(2, '0');
		return `${endYear}-${endMonth}-${endDay}`;
	})();

	// Load preview when leave date changes
	useEffect(() => {
		const loadPreview = async () => {
			if (!leaveDate || !token) {
				setPreview(null);
				return;
			}

			setPreviewLoading(true);
			try {
				const previewData = await getEmployeeTerminationPreview(token, {
					userId: user.id,
					companyLeaveDate: leaveDate
				});
				setPreview(previewData);
			} catch (error) {
				setPreview(null);
			} finally {
				setPreviewLoading(false);
			}
		};

		loadPreview();
	}, [leaveDate, token, user.id]);

	/**
	 * Valida el contacto que se está pidiendo. El correo es obligatorio cuando falta —
	 * sin él no hay recuperación de cuenta posible—; el teléfono solo se valida si el
	 * admin lo escribe, porque es un respaldo opcional.
	 */
	const validateContact = (normalizedEmail: string, normalizedPhone: string): boolean => {
		let valid = true;

		if (needsPersonalEmail) {
			if (!normalizedEmail) {
				setPersonalEmailError(translate('admin.employeeLeave.contact.emailRequired'));
				valid = false;
			} else if (!validatePersonalEmail(normalizedEmail)) {
				setPersonalEmailError(translate('admin.employeeLeave.contact.emailInvalid'));
				valid = false;
			} else if (normalizedEmail === user.email?.trim().toLowerCase()) {
				// Guardar el corporativo dejaría la baja correcta y al empleado
				// incomunicable el día que pierda esa cuenta.
				setPersonalEmailError(translate('admin.employeeLeave.contact.emailSameAsCorporate'));
				valid = false;
			}
		}

		if (needsPhone && normalizedPhone && !validatePhone(normalizedPhone)) {
			setPhoneError(translate('admin.employeeLeave.contact.phoneInvalid'));
			valid = false;
		}

		return valid;
	};

	const handleSubmit = async () => {
		if (!leaveDate) return;

		// Se normaliza antes de validar y de enviar: el backend guarda el correo tal cual
		// llega y la columna es UNIQUE, así que un espacio de más crearía un duplicado
		// que el propio admin no vería.
		const normalizedEmail = personalEmail.trim().toLowerCase();
		const normalizedPhone = phone.trim();

		// El aviso del intento anterior se limpia ANTES del corte por validación local:
		// si no, un error del servidor sigue en pantalla acusando algo que ya no aplica.
		setErrorMessage('');
		setPersonalEmailError('');
		setPhoneError('');
		if (!validateContact(normalizedEmail, normalizedPhone)) return;

		setIsLoading(true);

		try {
			const data = await employeeSoftDelete(token, {
				userId: user.id,
				companyLeaveDate: leaveDate,
				leaveReason: LeaveReasonType.ADMIN_TERMINATED,
				...(needsPersonalEmail && normalizedEmail ? { personalEmail: normalizedEmail } : {}),
				...(needsPhone && normalizedPhone ? { phone: normalizedPhone } : {})
			});

			if (data.error) {
				throw new Error(data.error);
			}

			toast({
				title: translate('admin.employeeLeave.success'),
				variant: 'default'
			});

			onSuccess();
			handleClose();
		} catch (error: any) {
			// El correo duplicado se señala en su propio campo, que es donde el admin
			// tiene que corregirlo; el resto de errores van al aviso general.
			if (error.message === PERSONAL_EMAIL_TAKEN) {
				setPersonalEmailError(translate('admin.employeeLeave.contact.emailTaken'));
			} else if (error.message === PERSONAL_EMAIL_SAME_AS_CORPORATE) {
				setPersonalEmailError(translate('admin.employeeLeave.contact.emailSameAsCorporate'));
			} else {
				const message = error.message || translate('admin.employeeLeave.error');
				setErrorMessage(message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setLeaveDate('');
		setErrorMessage('');
		setPreview(null);
		setPersonalEmail('');
		setPhone('');
		setPersonalEmailError('');
		setPhoneError('');
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{translate('admin.employeeLeave.modalTitle')}</DialogTitle>
					<DialogDescription>{translate('admin.employeeLeave.userInfo')}</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* User Information */}
					<div className="rounded-lg bg-muted/50 p-4">
						<p className="text-sm">
							<span className="font-medium">{translate('admin.employeeLeave.name')}:</span>{' '}
							{user.name}
						</p>
						<p className="text-sm">
							<span className="font-medium">{translate('admin.employeeLeave.email')}:</span>{' '}
							{user.email}
						</p>
					</div>

					{/* Date Input */}
					<div className="space-y-2">
						<Label htmlFor="leaveDate">{translate('admin.employeeLeave.leaveDate')} *</Label>
						<Input
							id="leaveDate"
							type="date"
							value={leaveDate}
							onChange={(e) => setLeaveDate(e.target.value)}
							min={today}
							max={endOfNextMonth}
						/>
					</div>

					{/* Captura del contacto personal: solo se pide lo que al empleado le falta */}
					{(needsPersonalEmail || needsPhone) && (
						<div className="rounded-lg border border-border">
							<p className="border-b border-border px-4 py-3 text-sm font-semibold">
								{translate('admin.employeeLeave.contact.title')}
							</p>
							<div className="space-y-4 p-4">
								<p className="text-sm text-muted-foreground">
									{translate(
										needsPersonalEmail && needsPhone
											? 'admin.employeeLeave.contact.descriptionBoth'
											: needsPersonalEmail
												? 'admin.employeeLeave.contact.descriptionEmail'
												: 'admin.employeeLeave.contact.descriptionPhone'
									)}
								</p>

								{needsPersonalEmail && (
									<div className="space-y-2">
										<Label htmlFor="personalEmail">
											{translate('admin.employeeLeave.contact.emailLabel')} *
										</Label>
										<Input
											id="personalEmail"
											type="email"
											value={personalEmail}
											placeholder={translate('admin.employeeLeave.contact.emailPlaceholder')}
											onChange={(e) => {
												setPersonalEmail(e.target.value);
												setPersonalEmailError('');
											}}
										/>
										{personalEmailError && (
											<p className="text-sm text-destructive">{personalEmailError}</p>
										)}
									</div>
								)}

								{needsPhone && (
									<div className="space-y-2">
										<Label htmlFor="personalPhone">
											{translate('admin.employeeLeave.contact.phoneLabel')}
										</Label>
										<Input
											id="personalPhone"
											type="tel"
											value={phone}
											placeholder={translate('admin.employeeLeave.contact.phonePlaceholder')}
											onChange={(e) => {
												setPhone(e.target.value);
												setPhoneError('');
											}}
										/>
										{phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Panel de aportaciones (diseño Penpot "div.panel") */}
					{leaveDate &&
						preview &&
						!previewLoading &&
						(() => {
							// CUERPO del panel (párrafo, subtítulo, empleado): según si la aportación del
							// mes de la baja ya se ejecutó.
							const scenario = getScenario(leaveDate, preview.contributionDay);
							// Si el empleado no tiene aportación propia configurada (importe base 0), su
							// aportación siempre es 0: se oculta la fila y el texto no la menciona.
							// Number() blinda frente a importes que el backend pueda enviar como string.
							const participantContributes = Number(preview.participantOriginalAmount ?? 0) > 0;
							const scenarioKey = (
								participantContributes
									? {
											RETRO: 'admin.employeeLeave.scenario.retro',
											FUTURE: 'admin.employeeLeave.scenario.future',
											CURRENT: 'admin.employeeLeave.scenario.current'
										}
									: {
											RETRO: 'admin.employeeLeave.scenario.retroNoParticipant',
											FUTURE: 'admin.employeeLeave.scenario.futureNoParticipant',
											CURRENT: 'admin.employeeLeave.scenario.currentNoParticipant'
										}
							)[scenario];
							// La aportación del empleado solo cuenta en CURRENT: ya se ejecutó (incluida su
							// retención). En RETRO y FUTURE aún no → 0,00 € atenuado.
							const showEmployeeContribution = scenario === 'CURRENT';
							const monthName = translate(`months.${preview.month}`);
							const employerAmount = Number(preview.employerAmount ?? 0);
							const employeeAmount =
								participantContributes && showEmployeeContribution
									? Number(preview.participantAmount ?? 0)
									: 0;
							const totalAmount = employerAmount + employeeAmount;
							// Subtítulo gris sobre la tabla (depende del escenario): CURRENT "APORTACIÓN
							// YA REALIZADA"; FUTURE "ÚLTIMA APORTACIÓN - {día} {mes}"; RETRO ninguno.
							const subtitle =
								scenario === 'FUTURE'
									? t('admin.employeeLeave.futureLastContribution', {
											contributionDay: preview.contributionDay,
											monthName
										})
									: scenario === 'CURRENT'
										? translate('admin.employeeLeave.currentSubtitle')
										: null;
							// TÍTULO/icono: según la relación de la fecha de baja con hoy (independiente del
							// cuerpo). Anterior → "fecha de baja pasada"; hoy → neutro "Información sobre la
							// baja" (sin icono); posterior → "fecha futura".
							const isToday = leaveDate === today;
							const panelTitle =
								leaveDate > today
									? translate('admin.employeeLeave.futureTitle')
									: isToday
										? translate('admin.employeeLeave.sameDayTitle')
										: translate('admin.employeeLeave.pastDateTitle');

							return (
								<div className="overflow-hidden rounded-2xl border border-[#dcdcdc] bg-white">
									{/* Cabecera (div.panel-head) */}
									<div className="flex items-center gap-2.5 border-b border-[#dcdcdc] bg-gray-50 px-[18px] py-3.5">
										{!isToday && (
											<AlertTriangle className="h-[22px] w-[22px] shrink-0 text-[#191c1c]" />
										)}
										<p className="font-heading text-[15px] font-semibold leading-tight text-[#191c1c]">
											{panelTitle}
										</p>
									</div>

									{/* Cuerpo (div.panel-body) */}
									<div className="space-y-4 p-[18px]">
										{/* Párrafo contextual por escenario. Si el empleado no tiene aportación
										    propia, se añade la aclaración como una frase más del mismo párrafo. */}
										<p className="text-xs leading-relaxed text-black">
											{t(scenarioKey, {
												leaveDate: formatDate(leaveDate),
												monthName,
												contributionDay: preview.contributionDay
											})}
											{!participantContributes &&
												` ${translate('admin.employeeLeave.participantNotConfigured')}`}
										</p>

										<div className="space-y-2">
											{/* Subtítulo (solo CURRENT y FUTURE) */}
											{subtitle && (
												<p className="text-xs font-semibold uppercase tracking-wide text-[#b9b9b9]">
													{subtitle}
												</p>
											)}

											{/* Tabla de aportaciones (div.table) */}
											<div className="overflow-hidden rounded-xl border border-[#eaeaea]">
												<div className="flex items-center justify-between border-b border-[#eaeaea] px-4 py-3">
													<span className="text-sm text-[#777777]">
														{translate('admin.employeeLeave.employerContribution')}
													</span>
													<span className="font-heading text-base font-medium text-[#191c1c]">
														{formatAmount(employerAmount)} €
													</span>
												</div>
												{/* Fila del empleado: solo si tiene aportación propia configurada */}
												{participantContributes && (
													<div className="flex items-center justify-between border-b border-[#eaeaea] px-4 py-3">
														<span
															className={`text-sm ${
																showEmployeeContribution ? 'text-[#777777]' : 'text-[#b9b9b9]'
															}`}
														>
															{translate('admin.employeeLeave.employeeContribution')}
														</span>
														<span
															className={`font-heading text-base font-medium ${
																showEmployeeContribution ? 'text-black' : 'text-[#b9b9b9]'
															}`}
														>
															{formatAmount(employeeAmount)} €
														</span>
													</div>
												)}
												<div className="flex items-center justify-between bg-[#fbfbfb] px-4 py-[13px]">
													<span className="text-sm font-semibold text-[#333333]">
														{translate('admin.employeeLeave.total')}
													</span>
													<span className="font-heading text-lg font-bold text-primary">
														{formatAmount(totalAmount)} €
													</span>
												</div>
											</div>
										</div>

										{/* Nota legal */}
										<p className="text-xs leading-relaxed text-[#777777]">
											{translate('admin.employeeLeave.legalNote')}{' '}
											<a
												href="mailto:soporte@arcadigital.app"
												className="text-primary hover:underline"
											>
												soporte@arcadigital.app
											</a>
										</p>
									</div>
								</div>
							);
						})()}

					{/* Loading Preview */}
					{leaveDate && previewLoading && (
						<div className="flex items-center justify-center py-4">
							<Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
							<span className="text-sm text-muted-foreground">
								{translate('admin.employeeLeave.loadingPreview')}
							</span>
						</div>
					)}

					{/* Error Message */}
					{errorMessage && (
						<div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4">
							<p className="text-sm text-destructive">{errorMessage}</p>
						</div>
					)}

					{/* Banner de confirmación por email */}
					<div className="rounded-lg border border-green-200 bg-green-50 p-3">
						<p className="text-sm text-center text-primary">
							{translate('admin.employeeLeave.emailConfirmation')}
						</p>
					</div>
				</div>

				<DialogFooter className="gap-2">
					<Button variant="outline" onClick={handleClose}>
						{translate('admin.employeeLeave.close')}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!leaveDate || isLoading}
						className="bg-primary hover:bg-primary/90"
					>
						{isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
						{isLoading
							? translate('admin.employeeLeave.processing')
							: translate('admin.employeeLeave.confirm')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EmployeeLeaveModalV2;
